// Web Audio API synthesized ringtone and chimes (No external asset files required)

let audioCtx: AudioContext | null = null;
let ringInterval: ReturnType<typeof setInterval> | null = null;

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

export const ringtonePlayer = {
  playIncomingRing: () => {
    try {
      const ctx = getAudioContext();
      if (ringInterval) return;

      const playTone = () => {
        const now = ctx.currentTime;
        // Two-tone European/standard phone ring: 440Hz + 480Hz
        const osc1 = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const gain = ctx.createGain();

        osc1.type = 'sine';
        osc1.frequency.setValueAtTime(440, now);
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(480, now);

        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.15, now + 0.1);
        gain.gain.setValueAtTime(0.15, now + 1.8);
        gain.gain.linearRampToValueAtTime(0, now + 2.0);

        osc1.connect(gain);
        osc2.connect(gain);
        gain.connect(ctx.destination);

        osc1.start(now);
        osc2.start(now);
        osc1.stop(now + 2.0);
        osc2.stop(now + 2.0);
      };

      playTone();
      ringInterval = setInterval(playTone, 4000);
    } catch (err) {
      console.warn('Unable to play synthesized ringtone:', err);
    }
  },

  playOutgoingChime: () => {
    try {
      const ctx = getAudioContext();
      if (ringInterval) return;

      const playBeep = () => {
        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(425, now);

        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.1, now + 0.05);
        gain.gain.setValueAtTime(0.1, now + 0.9);
        gain.gain.linearRampToValueAtTime(0, now + 1.0);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 1.0);
      };

      playBeep();
      ringInterval = setInterval(playBeep, 3000);
    } catch (err) {
      console.warn('Unable to play outgoing chime:', err);
    }
  },

  playEndBeep: () => {
    try {
      const ctx = getAudioContext();
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(480, now);
      osc.frequency.exponentialRampToValueAtTime(240, now + 0.3);

      gain.gain.setValueAtTime(0.15, now);
      gain.gain.linearRampToValueAtTime(0, now + 0.3);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + 0.3);
    } catch (e) {
      // ignore
    }
  },

  playEmergencyAlarm: () => {
    try {
      const ctx = getAudioContext();
      if (ringInterval) return;

      const playSiren = () => {
        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(800, now);
        osc.frequency.linearRampToValueAtTime(1200, now + 0.4);
        osc.frequency.linearRampToValueAtTime(800, now + 0.8);

        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.2, now + 0.05);
        gain.gain.setValueAtTime(0.2, now + 0.75);
        gain.gain.linearRampToValueAtTime(0, now + 0.8);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 0.8);
      };

      playSiren();
      ringInterval = setInterval(playSiren, 1200);
    } catch (err) {
      console.warn('Unable to play synthesized emergency alarm:', err);
    }
  },

  /**
   * Play a short, subtle notification chime.
   * Uses two sine oscillators (D5 = 587Hz, A5 = 880Hz) for a pleasant ping.
   * Does NOT force-resume AudioContext — silently skips if not yet unlocked.
   * Caller is responsible for debouncing (max once per 2s recommended).
   */
  playNotificationChime: () => {
    try {
      if (!audioCtx) return; // No user gesture yet — skip silently
      if (audioCtx.state === 'suspended') return; // Still locked — skip silently
      const ctx = audioCtx;
      const now = ctx.currentTime;

      // First tone: D5 (587 Hz)
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(587, now);
      gain1.gain.setValueAtTime(0, now);
      gain1.gain.linearRampToValueAtTime(0.06, now + 0.02);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(now);
      osc1.stop(now + 0.25);

      // Second tone: A5 (880 Hz), starts slightly after
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(880, now + 0.12);
      gain2.gain.setValueAtTime(0, now + 0.12);
      gain2.gain.linearRampToValueAtTime(0.06, now + 0.14);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.40);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(now + 0.12);
      osc2.stop(now + 0.40);
    } catch (err) {
      // Silently swallow — notification chime is non-critical
    }
  },

  stop: () => {
    if (ringInterval) {
      clearInterval(ringInterval);
      ringInterval = null;
    }
  }
};
